import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import {
  Card,
  EmptyState,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

// ExpenseDistribution — histogram of expense amount buckets. Bucket edges
// are tuned for small-business expense patterns (coffee runs through
// annual SaaS contracts). `data-testid` on each bar so e2e can count or
// label-probe without depending on recharts' DOM layout.

interface Bucket {
  id: string;
  label: string;
  min: number; // inclusive, cents
  max: number | null; // exclusive, cents; null = open-ended
  color: string;
}

const BUCKETS: Bucket[] = [
  { id: "tier-0-25", label: "$0–25", min: 0, max: 2500, color: "#C4B5FD" },
  { id: "tier-25-100", label: "$25–100", min: 2500, max: 10000, color: "#A78BFA" },
  { id: "tier-100-500", label: "$100–500", min: 10000, max: 50000, color: "#8B5CF6" },
  { id: "tier-500-2000", label: "$500–2k", min: 50000, max: 200000, color: "#7C3AED" },
  { id: "tier-2000-plus", label: "$2k+", min: 200000, max: null, color: "#6D28D9" },
];

function bucketFor(amountCents: number): Bucket | null {
  for (const b of BUCKETS) {
    const withinMax = b.max == null || amountCents < b.max;
    if (amountCents >= b.min && withinMax) return b;
  }
  return null;
}

export function ExpenseDistribution({
  preset,
  submittedBy,
}: {
  preset: RangePreset;
  submittedBy?: string | null;
}) {
  const { expenses } = useExpenses();

  const data = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const counts = new Map<string, number>(BUCKETS.map((b) => [b.id, 0]));
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      const b = bucketFor(e.amountCents);
      if (!b) continue;
      counts.set(b.id, (counts.get(b.id) ?? 0) + 1);
    }
    return BUCKETS.map((b) => ({
      bucket: b.label,
      id: b.id,
      count: counts.get(b.id) ?? 0,
      color: b.color,
    }));
  }, [expenses, preset, submittedBy]);

  const hasAny = data.some((d) => d.count > 0);

  return (
    <Card
      title="Expense distribution"
      testId="report-expense-distribution"
    >
      {!hasAny ? (
        <EmptyState message="No expenses in this window yet." />
      ) : (
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
            >
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 11, fill: "#334155" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                cursor={{ fill: "rgba(124,58,237,0.05)" }}
                formatter={(v: number) => [`${v} expense${v === 1 ? "" : "s"}`, "Count"]}
                labelFormatter={(label) => `Bucket: ${label}`}
              />
              <Bar dataKey="count" isAnimationActive={false} radius={[4, 4, 0, 0]}>
                {data.map((d) => (
                  <Cell
                    key={d.id}
                    fill={d.color}
                    data-testid={`expense-distribution-bar-${d.id}`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
